import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { CheckCircle2, XCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  PUNTOS_DEFAULT,
  renderCurvaCaudalToDataURL,
} from '@/lib/checklists/curvaCaudalChart';
import {
  type UnidadCaudal,
  type UnidadPresion,
  labelCaudal,
  labelPresion,
  convertCaudal,
  roundForUnit,
} from '@/lib/checklists/units';
import { evaluarAlertasBomba, contarAnomalias, type AlertaPrueba } from '@/lib/checklists/pruebaPrestacionesAlertas';

export type ResultadoPrueba = 'positivo' | 'negativo' | null;
export type TipoBomba = 'electrica' | 'diesel';

export interface PuntoPrueba {
  pct: number;
  /** Caudal leído en la unidad del caudalímetro (instrumento). */
  caudal: number;
  /** Presión leída en la unidad del manómetro (instrumento). */
  presion: number;
}

export interface UnidadesBomba {
  placa:       { caudal: UnidadCaudal; presion: UnidadPresion };
  instrumento: { caudal: UnidadCaudal; presion: UnidadPresion };
}

export interface PruebaBombaData {
  elemento_id: string;
  nombre: string;
  tipo: TipoBomba;
  obsoleta?: boolean;
  unidades: UnidadesBomba;
  /** Datos de placa del fabricante, en las unidades de placa. */
  nominal: { caudal: number; presion: number };
  /** Puntos medidos, en unidades del instrumento. */
  puntos: PuntoPrueba[];
  resultado: ResultadoPrueba;
  observaciones: string;
}

export interface PruebaPrestacionesValue {
  bombas: Record<string, PruebaBombaData>;
}

export interface BombaInventario {
  id: string;
  nombre: string;
  tipo: TipoBomba;
  /** Caudal nominal según placa, en `unidadCaudalPlaca`. */
  nominalCaudal?: number;
  /** Presión nominal según placa, en `unidadPresionPlaca`. */
  nominalPresion?: number;
  unidadCaudalPlaca?: UnidadCaudal;
  unidadPresionPlaca?: UnidadPresion;
}

export interface InstrumentosSala {
  caudalimetro?: { unidad: UnidadCaudal; nombre?: string };
  manometro?: { unidad: UnidadPresion; nombre?: string };
}

const DEFAULT_UNIDADES: UnidadesBomba = {
  placa:       { caudal: 'lpm', presion: 'bar' },
  instrumento: { caudal: 'lpm', presion: 'bar' },
};

const recomputeCaudalPuntos = (
  nominalCaudal: number,
  uPlaca: UnidadCaudal,
  uInstr: UnidadCaudal,
  puntos: PuntoPrueba[],
): PuntoPrueba[] => {
  const nominalInstr = convertCaudal(nominalCaudal || 0, uPlaca, uInstr);
  return puntos.map(p => ({
    ...p,
    caudal: roundForUnit((nominalInstr * p.pct) / 100),
  }));
};

const emptyBomba = (b: BombaInventario, instr: InstrumentosSala): PruebaBombaData => {
  const unidades: UnidadesBomba = {
    placa: {
      caudal: b.unidadCaudalPlaca || 'lpm',
      presion: b.unidadPresionPlaca || 'bar',
    },
    instrumento: {
      caudal: instr.caudalimetro?.unidad || b.unidadCaudalPlaca || 'lpm',
      presion: instr.manometro?.unidad || b.unidadPresionPlaca || 'bar',
    },
  };
  const nominal = {
    caudal: Number(b.nominalCaudal) || 0,
    presion: Number(b.nominalPresion) || 0,
  };
  const puntos = recomputeCaudalPuntos(
    nominal.caudal,
    unidades.placa.caudal,
    unidades.instrumento.caudal,
    PUNTOS_DEFAULT.map(pct => ({ pct, caudal: 0, presion: 0 })),
  );
  return {
    elemento_id: b.id,
    nombre: b.nombre,
    tipo: b.tipo,
    unidades,
    nominal,
    puntos,
    resultado: null,
    observaciones: '',
  };
};

export function emptyPruebaPrestaciones(): PruebaPrestacionesValue {
  return { bombas: {} };
}

/** Reconciliación con el set de % vigente: añade filas que faltan, quita las que sobran, mantiene valores tecleados. */
const reconcilePuntos = (existentes: PuntoPrueba[] | undefined): PuntoPrueba[] => {
  const mapa = new Map<number, PuntoPrueba>();
  (existentes || []).forEach(p => mapa.set(p.pct, p));
  return PUNTOS_DEFAULT.map(pct => mapa.get(pct) || { pct, caudal: 0, presion: 0 });
};

/** Migra estructura legacy al nuevo formato. */
export function normalizePruebaPrestaciones(raw: any): PruebaPrestacionesValue {
  if (!raw || typeof raw !== 'object') return emptyPruebaPrestaciones();

  const normalizeBomba = (b: any, fallbackNombre: string, fallbackTipo: TipoBomba): PruebaBombaData => {
    const unidades: UnidadesBomba = b?.unidades
      ? {
          placa: {
            caudal: (b.unidades.placa?.caudal as UnidadCaudal) || 'lpm',
            presion: (b.unidades.placa?.presion as UnidadPresion) || 'bar',
          },
          instrumento: {
            caudal: (b.unidades.instrumento?.caudal as UnidadCaudal) || 'lpm',
            presion: (b.unidades.instrumento?.presion as UnidadPresion) || 'bar',
          },
        }
      : DEFAULT_UNIDADES;
    const nominal = b?.nominal
      ? {
          caudal: Number(b.nominal.caudal ?? b.nominal.caudal_lpm ?? 0) || 0,
          presion: Number(b.nominal.presion ?? b.nominal.presion_bar ?? 0) || 0,
        }
      : { caudal: 0, presion: 0 };
    const puntosRaw: PuntoPrueba[] = Array.isArray(b?.puntos)
      ? b.puntos.map((p: any) => ({
          pct: Number(p.pct) || 0,
          caudal: Number(p.caudal ?? p.caudal_lpm ?? 0) || 0,
          presion: Number(p.presion ?? p.presion_bar ?? 0) || 0,
        }))
      : [];
    return {
      elemento_id: b?.elemento_id || fallbackNombre,
      nombre: b?.nombre || fallbackNombre,
      tipo: (b?.tipo as TipoBomba) || fallbackTipo,
      obsoleta: !!b?.obsoleta,
      unidades,
      nominal,
      puntos: reconcilePuntos(puntosRaw),
      resultado: (b?.resultado as ResultadoPrueba) ?? null,
      observaciones: b?.observaciones || '',
    };
  };

  if (raw.bombas && typeof raw.bombas === 'object') {
    const bombas: Record<string, PruebaBombaData> = {};
    Object.entries(raw.bombas as Record<string, any>).forEach(([id, b]) => {
      bombas[id] = { ...normalizeBomba(b, b?.nombre || 'Bomba', (b?.tipo as TipoBomba) || 'electrica'), elemento_id: id };
    });
    return { bombas };
  }

  const bombas: Record<string, PruebaBombaData> = {};
  if (raw.bomba_electrica) {
    const k = '__legacy_electrica__';
    bombas[k] = { ...normalizeBomba(raw.bomba_electrica, 'Bomba principal eléctrica (legacy)', 'electrica'), elemento_id: k, obsoleta: true };
  }
  if (raw.bomba_diesel) {
    const k = '__legacy_diesel__';
    bombas[k] = { ...normalizeBomba(raw.bomba_diesel, 'Bomba principal diésel (legacy)', 'diesel'), elemento_id: k, obsoleta: true };
  }
  return { bombas };
}


const BombaPanel: React.FC<{
  data: PruebaBombaData;
  onChange: (d: PruebaBombaData) => void;
  disabled?: boolean;
}> = ({ data, onChange, disabled }) => {
  const uCaudalInstr = data.unidades.instrumento.caudal;
  const uPresionInstr = data.unidades.instrumento.presion;
  const uCaudalPlaca = data.unidades.placa.caudal;
  const uPresionPlaca = data.unidades.placa.presion;

  const setPunto = (idx: number, key: 'caudal' | 'presion', v: number) => {
    const puntos = data.puntos.map((p, i) => (i === idx ? { ...p, [key]: v } : p));
    onChange({ ...data, puntos });
  };

  const alertas = useMemo(() => evaluarAlertasBomba(data), [data]);
  const alertasPorPct = useMemo(() => {
    const map = new Map<number, AlertaPrueba['nivel']>();
    alertas.forEach(a => {
      if (a.pct == null) return;
      const prev = map.get(a.pct);
      // anomalia > warning > info
      const rank = { info: 0, warning: 1, anomalia: 2 } as const;
      if (!prev || rank[a.nivel] > rank[prev]) map.set(a.pct, a.nivel);
    });
    return map;
  }, [alertas]);

  const chartUrl = useMemo(() => {
    try {
      return renderCurvaCaudalToDataURL({
        nominal: data.nominal,
        puntos: data.puntos,
        ancho: 720,
        alto: 360,
        titulo: data.nombre,
        unidadCaudalLabel: labelCaudal(uCaudalInstr),
        unidadPresionLabel: labelPresion(uPresionInstr),
        unidadCaudalPlaca: uCaudalPlaca,
        unidadPresionPlaca: uPresionPlaca,
        unidadCaudalInstrumento: uCaudalInstr,
        unidadPresionInstrumento: uPresionInstr,
      });
    } catch { return ''; }
  }, [data.nominal, data.puntos, data.nombre, uCaudalInstr, uPresionInstr, uCaudalPlaca, uPresionPlaca]);

  return (
    <div className="space-y-4 p-1">
      {/* Resumen de placa + instrumentos (datos heredados del inventario) */}
      <div className="rounded-md border bg-muted/30 p-3 text-xs space-y-1">
        <div>
          <span className="font-semibold">Placa: </span>
          {data.nominal.caudal || '—'} {labelCaudal(uCaudalPlaca)}
          {' @ '}
          {data.nominal.presion || '—'} {labelPresion(uPresionPlaca)}
        </div>
        <div className="text-muted-foreground">
          Instrumentos en sala: caudalímetro en <strong>{labelCaudal(uCaudalInstr)}</strong>,
          {' '}manómetro en <strong>{labelPresion(uPresionInstr)}</strong>.
        </div>
        <div className="text-[10px] text-muted-foreground">
          Configurado en el alta del elemento. Si cambia, edítalo en inventario.
        </div>
      </div>

      {/* Tabla de puntos */}
      <div className="rounded-md border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left px-3 py-2 w-[90px]">% caudal</th>
              <th className="text-left px-3 py-2">Caudalímetro ({labelCaudal(uCaudalInstr)})</th>
              <th className="text-left px-3 py-2">Manómetro ({labelPresion(uPresionInstr)})</th>
            </tr>
          </thead>
          <tbody>
            {data.puntos.map((p, i) => {
              const nivel = alertasPorPct.get(p.pct);
              const rowAccent =
                nivel === 'anomalia' ? 'border-l-4 border-l-destructive bg-destructive/5'
                : nivel === 'warning' ? 'border-l-4 border-l-amber-500 bg-amber-500/5'
                : '';
              return (
                <tr key={p.pct} className={cn('border-t', rowAccent)}>
                  <td className="px-3 py-2 font-mono font-semibold">
                    {p.pct}%
                    <div className="text-[10px] text-muted-foreground font-sans font-normal">
                      teórico: {p.caudal || '—'}
                    </div>
                  </td>
                  <td className="px-2 py-1">
                    <Input
                      type="number"
                      inputMode="decimal"
                      value={p.caudal || ''}
                      onChange={(e) => setPunto(i, 'caudal', Number(e.target.value) || 0)}
                      disabled={disabled}
                      className="h-9"
                    />
                  </td>
                  <td className="px-2 py-1">
                    <Input
                      type="number"
                      inputMode="decimal"
                      value={p.presion || ''}
                      onChange={(e) => setPunto(i, 'presion', Number(e.target.value) || 0)}
                      disabled={disabled}
                      className="h-9"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Alertas y comprobaciones automáticas */}
      {alertas.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Comprobaciones automáticas
          </div>
          {alertas.map(a => {
            const styles = a.nivel === 'anomalia'
              ? 'bg-destructive/10 border-destructive/40 text-destructive'
              : a.nivel === 'warning'
              ? 'bg-amber-500/10 border-amber-500/40 text-amber-800 dark:text-amber-300'
              : 'bg-muted/60 border-muted-foreground/20 text-foreground';
            const Icon = a.nivel === 'anomalia' ? AlertTriangle : a.nivel === 'warning' ? AlertTriangle : Info;
            const chip =
              a.destinoPDF === 'anomalia' ? { label: '→ PDF: ANOMALÍA', cls: 'bg-destructive text-destructive-foreground' }
              : a.destinoPDF === 'alerta' ? { label: '→ PDF: alerta', cls: 'bg-amber-500 text-white' }
              : null;
            return (
              <div key={a.id} className={cn('rounded-md border px-3 py-2 text-xs flex items-start gap-2', styles)}>
                <Icon className="h-4 w-4 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <div>{a.mensaje}</div>
                  {a.pct != null && (
                    <div className="text-[10px] opacity-70 mt-0.5">Punto {a.pct} %</div>
                  )}
                </div>
                {chip && (
                  <span className={cn('text-[10px] px-2 py-0.5 rounded-full font-semibold shrink-0', chip.cls)}>
                    {chip.label}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {chartUrl && (
        <div className="rounded-md border bg-white p-2">
          <img src={chartUrl} alt={`Curva ${data.nombre}`} className="w-full h-auto" />
        </div>
      )}

      <div className="space-y-2">
        <Label className="text-xs">Resultado de la prueba</Label>
        <div className="grid grid-cols-2 gap-2">
          <Button
            type="button"
            variant={data.resultado === 'positivo' ? 'default' : 'outline'}
            disabled={disabled}
            onClick={() => onChange({ ...data, resultado: 'positivo' })}
            className={cn('h-12', data.resultado === 'positivo' && 'bg-green-600 hover:bg-green-700 text-white')}
          >
            <CheckCircle2 className="h-5 w-5 mr-2" /> POSITIVO
          </Button>
          <Button
            type="button"
            variant={data.resultado === 'negativo' ? 'default' : 'outline'}
            disabled={disabled}
            onClick={() => onChange({ ...data, resultado: 'negativo' })}
            className={cn('h-12', data.resultado === 'negativo' && 'bg-red-600 hover:bg-red-700 text-white')}
          >
            <XCircle className="h-5 w-5 mr-2" /> NEGATIVO
          </Button>
        </div>
      </div>

      <div className="space-y-1">
        <Label className="text-xs">Observaciones del técnico</Label>
        <Textarea
          value={data.observaciones}
          onChange={(e) => onChange({ ...data, observaciones: e.target.value })}
          disabled={disabled}
          placeholder="Comentarios sobre la curva, desviaciones, condiciones de prueba..."
          rows={2}
        />
      </div>
    </div>
  );
};

interface Props {
  value: PruebaPrestacionesValue;
  onChange: (v: PruebaPrestacionesValue) => void;
  bombasInventario: BombaInventario[];
  instrumentos?: InstrumentosSala;
  disabled?: boolean;
}

export const PruebaPrestacionesBlock: React.FC<Props> = ({ value, onChange, bombasInventario, instrumentos, disabled }) => {
  const instr = instrumentos || {};
  // Reconciliación de inventario + de los puntos por bomba.
  useEffect(() => {
    const inventarioIds = new Set(bombasInventario.map(b => b.id));
    const current = value.bombas || {};
    let changed = false;
    const next: Record<string, PruebaBombaData> = {};

    Object.entries(current).forEach(([id, b]) => {
      const reconciled = reconcilePuntos(b.puntos);
      const samePuntos = reconciled.length === (b.puntos?.length || 0)
        && reconciled.every((p, i) => p.pct === b.puntos[i].pct && p.caudal === b.puntos[i].caudal && p.presion === b.puntos[i].presion);
      next[id] = samePuntos ? b : { ...b, puntos: reconciled };
      if (!samePuntos) changed = true;
    });

    bombasInventario.forEach(b => {
      if (!next[b.id]) {
        next[b.id] = emptyBomba(b, instr);
        changed = true;
        return;
      }
      // Sincroniza nombre / tipo / unidades / nominal desde inventario.
      const cur = next[b.id];
      const targetUnidades: UnidadesBomba = {
        placa: {
          caudal: b.unidadCaudalPlaca || cur.unidades.placa.caudal,
          presion: b.unidadPresionPlaca || cur.unidades.placa.presion,
        },
        instrumento: {
          caudal: instr.caudalimetro?.unidad || cur.unidades.instrumento.caudal,
          presion: instr.manometro?.unidad || cur.unidades.instrumento.presion,
        },
      };
      const targetNominal = {
        caudal: Number(b.nominalCaudal) || cur.nominal.caudal,
        presion: Number(b.nominalPresion) || cur.nominal.presion,
      };
      const unidadesChanged =
        targetUnidades.placa.caudal !== cur.unidades.placa.caudal ||
        targetUnidades.placa.presion !== cur.unidades.placa.presion ||
        targetUnidades.instrumento.caudal !== cur.unidades.instrumento.caudal ||
        targetUnidades.instrumento.presion !== cur.unidades.instrumento.presion;
      const nominalChanged =
        targetNominal.caudal !== cur.nominal.caudal ||
        targetNominal.presion !== cur.nominal.presion;
      const metaChanged = cur.nombre !== b.nombre || cur.tipo !== b.tipo || cur.obsoleta;
      if (unidadesChanged || nominalChanged || metaChanged) {
        const puntos = (unidadesChanged || nominalChanged)
          ? recomputeCaudalPuntos(
              targetNominal.caudal,
              targetUnidades.placa.caudal,
              targetUnidades.instrumento.caudal,
              cur.puntos,
            )
          : cur.puntos;
        next[b.id] = {
          ...cur,
          nombre: b.nombre,
          tipo: b.tipo,
          obsoleta: false,
          unidades: targetUnidades,
          nominal: targetNominal,
          puntos,
        };
        changed = true;
      }
    });
    Object.keys(current).forEach(id => {
      if (!inventarioIds.has(id) && next[id] && !next[id].obsoleta) {
        next[id] = { ...next[id], obsoleta: true };
        changed = true;
      }
    });
    if (changed) onChange({ bombas: next });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bombasInventario.map(b => `${b.id}:${b.nombre}:${b.tipo}:${b.nominalCaudal}:${b.nominalPresion}:${b.unidadCaudalPlaca}:${b.unidadPresionPlaca}`).join('|'),
    instr.caudalimetro?.unidad,
    instr.manometro?.unidad,
  ]);

  const todas = Object.values(value.bombas || {});
  const electricas = todas.filter(b => b.tipo === 'electrica');
  const diesel = todas.filter(b => b.tipo === 'diesel');

  const [tab, setTab] = useState<TipoBomba>(electricas.length > 0 ? 'electrica' : 'diesel');

  if (bombasInventario.length === 0 && todas.filter(b => !b.obsoleta).length === 0) {
    return (
      <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
        <AlertCircle className="h-4 w-4 mt-0.5 text-amber-600 shrink-0" />
        <div>
          Añade al menos una <strong>bomba principal eléctrica</strong> o <strong>diésel</strong> al inventario del sistema
          para registrar la prueba de prestaciones (una curva por bomba).
        </div>
      </div>
    );
  }

  const renderLista = (lista: PruebaBombaData[]) => {
    if (lista.length === 0) {
      return <p className="text-xs text-muted-foreground px-1 py-3">No hay bombas de este tipo en el inventario.</p>;
    }
    return (
      <Accordion type="multiple" defaultValue={lista.map(b => b.elemento_id)} className="space-y-2">
        {lista.map(b => {
          const nAnom = contarAnomalias(evaluarAlertasBomba(b));
          return (
          <AccordionItem key={b.elemento_id} value={b.elemento_id} className="rounded-lg border bg-card">
            <AccordionTrigger className="px-4 py-3 hover:no-underline">
              <div className="flex items-center gap-2 flex-1 text-left flex-wrap">
                <span className="font-semibold text-sm">{b.nombre}</span>
                {b.obsoleta && (
                  <Badge variant="outline" className="text-[10px]">obsoleta</Badge>
                )}
                {nAnom > 0 && (
                  <Badge className="bg-destructive text-destructive-foreground text-[10px]">
                    <AlertTriangle className="h-3 w-3 mr-1" /> {nAnom} {nAnom === 1 ? 'anomalía' : 'anomalías'}
                  </Badge>
                )}
                {b.resultado === 'positivo' && (
                  <Badge className="bg-green-500/20 text-green-700 border-green-500/30">
                    <CheckCircle2 className="h-3 w-3 mr-1" /> POSITIVO
                  </Badge>
                )}
                {b.resultado === 'negativo' && (
                  <Badge className="bg-red-500/20 text-red-700 border-red-500/30">
                    <XCircle className="h-3 w-3 mr-1" /> NEGATIVO
                  </Badge>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <BombaPanel
                data={b}
                onChange={(d) => onChange({ bombas: { ...value.bombas, [b.elemento_id]: d } })}
                disabled={disabled || b.obsoleta}
              />
            </AccordionContent>
          </AccordionItem>
          );
        })}
      </Accordion>
    );
  };

  const showElectricas = electricas.length > 0;
  const showDiesel = diesel.length > 0;

  const faltaCaudalimetro = !instr.caudalimetro;
  const faltaManometro = !instr.manometro;
  const bombasSinDatos = bombasInventario.filter(
    b => !Number(b.nominalCaudal) || !Number(b.nominalPresion),
  );
  const hayPrerequisitosFaltantes =
    faltaCaudalimetro || faltaManometro || bombasSinDatos.length > 0;

  return (
    <div className="space-y-3">
      {hayPrerequisitosFaltantes && (
        <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
          <AlertCircle className="h-4 w-4 mt-0.5 text-amber-600 shrink-0" />
          <div className="space-y-1">
            <div className="font-semibold">
              Para realizar la curva de prestaciones necesitas:
            </div>
            <ul className="list-disc pl-5 space-y-0.5">
              {faltaCaudalimetro && (
                <li>
                  Añadir un <strong>caudalímetro</strong> al inventario del sistema
                  (e indicar la unidad de medida).
                </li>
              )}
              {faltaManometro && (
                <li>
                  Añadir un <strong>manómetro de impulsión</strong>{' '}
                  al inventario del sistema (e indicar la unidad de medida).
                </li>
              )}
              {bombasSinDatos.length > 0 && (
                <li>
                  Rellenar la <strong>presión nominal</strong> y el{' '}
                  <strong>caudal nominal</strong> (datos de placa) en{' '}
                  {bombasSinDatos.length === 1
                    ? 'la bomba'
                    : 'las bombas'}{' '}
                  <strong>{bombasSinDatos.map(b => b.nombre).join(', ')}</strong>.
                </li>
              )}
            </ul>
          </div>
        </div>
      )}
      <div className="flex gap-2">
        {showElectricas && (
          <Button
            type="button"
            variant={tab === 'electrica' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setTab('electrica')}
          >
            Eléctricas ({electricas.length})
          </Button>
        )}
        {showDiesel && (
          <Button
            type="button"
            variant={tab === 'diesel' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setTab('diesel')}
          >
            Diésel ({diesel.length})
          </Button>
        )}
      </div>

      {tab === 'electrica' ? renderLista(electricas) : renderLista(diesel)}
    </div>
  );
};
